import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { BRAND_LOGO, formatMoney } from "../lib/brand";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { ImageUpload } from "../components/brand/ImageUpload";
import { toast } from "sonner";
import { CheckCircle, Hourglass, Warning, ArrowClockwise } from "@phosphor-icons/react";

export default function MembershipPayment() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [screenshot, setScreenshot] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "member") { nav("/"); return; }
    api.get("/settings/payment").then(({ data }) => setSettings(data));
    api.get("/membership/my-status").then(({ data }) => setStatus(data));
  }, [user, nav]);

  const submit = async () => {
    if (!screenshot) { toast.error("Upload your payment screenshot first"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/membership/submit-payment", { screenshot_url: screenshot });
      setStatus({ ...status, status: data.status, payment_screenshot_url: data.payment_screenshot_url });
      toast.success("Payment submitted. Awaiting verification.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!settings || !status) return <div className="min-h-screen bg-[#050505]"><BrandHeader /><div className="px-6 py-16 text-white/60">Loading…</div></div>;

  const renderStatusBanner = () => {
    if (status.status === "active") return (
      <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 p-5 rounded-md flex gap-3 items-start" data-testid="status-active">
        <CheckCircle size={24} weight="duotone" />
        <div>
          <p className="font-medium">Membership Active</p>
          <p className="text-sm opacity-90 mt-1">Your Social Circle membership is approved. Visit your member portal to view your branded QR code.</p>
          <Link to="/me" className="tsc-btn-gold inline-block mt-4">Open my member portal</Link>
        </div>
      </div>
    );
    if (status.status === "pending_verification") return (
      <div className="border border-amber-500/40 bg-amber-500/10 text-amber-200 p-5 rounded-md flex gap-3 items-start" data-testid="status-pending-verification">
        <Hourglass size={24} weight="duotone" />
        <div>
          <p className="font-medium">Pending Verification</p>
          <p className="text-sm opacity-90 mt-1">Your payment screenshot has been received and is under review by our team. You'll get full member access once approved.</p>
        </div>
      </div>
    );
    if (status.status === "rejected") return (
      <div className="border border-red-500/40 bg-red-500/10 text-red-200 p-5 rounded-md flex gap-3 items-start" data-testid="status-rejected">
        <Warning size={24} weight="duotone" />
        <div>
          <p className="font-medium">Membership not approved</p>
          {status.rejection_reason && <p className="text-sm opacity-90 mt-1">{status.rejection_reason}</p>}
          <p className="text-sm opacity-80 mt-2">Please review the reason above and submit a new payment.</p>
        </div>
      </div>
    );
    return null;
  };

  const canSubmit = status.status === "pending_payment" || status.status === "rejected";

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <section className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-10 rise-in">
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Activate your membership</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-white">One step away from the Circle.</h1>
          <p className="text-white/60 mt-3">Complete your GCash payment, upload the screenshot, and our team will verify your membership.</p>
        </div>

        {renderStatusBanner()}

        {canSubmit && (
          <>
            <div className="grid md:grid-cols-2 gap-8 mt-8">
              {/* GCash QR + details */}
              <div className="tsc-card p-6" data-testid="gcash-card">
                <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Step 1 · Pay via GCash</p>
                <div className="font-display text-3xl text-white mt-3">{formatMoney(settings.membership_price)}</div>
                <p className="text-white/55 text-sm">Annual membership · valid until December 31</p>
                <div className="mt-5 bg-white p-3 rounded-md inline-block">
                  {settings.gcash_qr_url ? (
                    <img src={settings.gcash_qr_url} alt="GCash QR" className="w-56 h-56 object-contain" />
                  ) : (
                    <div className="w-56 h-56 flex items-center justify-center text-black/50 text-xs text-center px-4">QR not configured. Use the GCash number below.</div>
                  )}
                </div>
                <div className="mt-5 space-y-1.5 text-sm">
                  {settings.gcash_account_name && <p className="text-white/70"><span className="text-white/40 mr-2">Name</span>{settings.gcash_account_name}</p>}
                  {settings.gcash_number && <p className="text-white/70"><span className="text-white/40 mr-2">GCash #</span><span className="font-mono">{settings.gcash_number}</span></p>}
                </div>
                {settings.payment_instructions && (
                  <p className="text-white/55 text-xs mt-5 leading-relaxed whitespace-pre-wrap">{settings.payment_instructions}</p>
                )}
              </div>

              {/* Upload screenshot */}
              <div className="tsc-card p-6">
                <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Step 2 · Upload screenshot</p>
                <p className="text-white/60 text-sm mt-2">JPG / PNG / WEBP only. Maximum 5MB.</p>
                <div className="mt-5">
                  <ImageUpload value={screenshot} onChange={setScreenshot} aspect="aspect-[3/4]" testId="payment-screenshot" />
                </div>
                <button onClick={submit} disabled={!screenshot || submitting} className="tsc-btn-gold w-full mt-6" data-testid="submit-payment-btn">
                  {submitting ? "Submitting…" : "Submit Payment for Verification"}
                </button>
                <p className="text-white/40 text-xs mt-4 italic">Your membership will become Active once our team verifies your payment.</p>
              </div>
            </div>

            {status.status === "rejected" && (
              <div className="mt-6 text-center">
                <button onClick={submit} disabled={!screenshot || submitting} className="tsc-btn-outline inline-flex items-center gap-2">
                  <ArrowClockwise size={14} /> Resubmit payment
                </button>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
